import { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { formatApiError } from "../lib/api";
import { useI18n } from "../context/I18nContext";

export default function Login() {
  const { login } = useAuth();
  const { t } = useI18n();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const user = await login(email, password);
      const dest = location.state?.from || (user.has_paid ? "/dashboard" : "/paywall");
      navigate(dest, { replace: true });
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-[calc(100vh-64px)] bg-[#F4F4F4] text-black flex items-center justify-center px-5 py-10" data-testid="login-page">
      <div className="w-full max-w-md bg-white border-2 border-black brutal-shadow p-8">
        <div className="font-mono-civic text-xs uppercase tracking-[0.3em] text-black">{t("auth_citizen_access")}</div>
        <h1 className="mt-2 font-serif-display font-extrabold text-4xl text-black leading-tight">{t("auth_login_h1")}</h1>
        <p className="mt-2 font-bold text-black">{t("auth_login_sub")}</p>

        <form onSubmit={handleSubmit} className="mt-8 space-y-5" data-testid="login-form">
          <div>
            <label className="block font-mono-civic text-xs uppercase tracking-widest text-black mb-2">{t("auth_email")}</label>
            <input
              type="email" required value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full border-2 border-black px-4 py-3 font-bold text-black bg-white focus:outline-none focus:border-[#E00000]"
              data-testid="login-email-input"
            />
          </div>
          <div>
            <label className="block font-mono-civic text-xs uppercase tracking-widest text-black mb-2">{t("auth_password")}</label>
            <input
              type="password" required minLength={6} value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full border-2 border-black px-4 py-3 font-bold text-black bg-white focus:outline-none focus:border-[#E00000]"
              data-testid="login-password-input"
            />
          </div>
          {error && (
            <div className="border-2 border-[#E00000] bg-white p-3 font-bold text-[#E00000] text-sm" data-testid="login-error">
              {error}
            </div>
          )}
          <button
            type="submit" disabled={loading}
            className="w-full bg-black text-white font-bold uppercase tracking-widest py-4 border-2 border-black brutal-shadow-sm hover:translate-x-[2px] hover:translate-y-[2px] hover:shadow-none transition-all disabled:opacity-60"
            data-testid="login-submit-btn"
          >
            {loading ? t("auth_login_loading") : t("auth_login_btn")}
          </button>
        </form>

        <p className="mt-6 font-bold text-black text-sm">
          {t("auth_no_account")}{" "}
          <Link to="/register" className="underline" data-testid="login-to-register">{t("auth_create_account_link")}</Link>
        </p>
      </div>
    </div>
  );
}
